// src/publish/serialize/plain.js — blocks → plain text (social composer, .txt).
//
// The text an operator pastes into a box that accepts no markup. Rules:
//   bold / italic   the text only
//   list item       "• item", numbered "1. item"
//   link            "testo (URL)" — just the URL when the text IS the URL
//   heading         on its own line
//   br              "\n"; blocks are separated by a blank line
//   cite "[N]"      DROPPED unless { cites: true } — together with the blank
//                   before it, so "persone [1]." reads "persone."
//   qa              question, newline, answer
//   slide           "Slide N · Tipo", headline, body, "Indicazione visiva: …"
// Nothing here contains Markdown or HTML: no asterisks, no tags. A block that
// lacks its items / runs (hand-built) gives empty output, never a throw.

import i18n from '../../i18n/index.js';
import { asRuns, asBlocks, dropCiteBlank } from '../blocks.js';

/** The items of a list block, [] when the block carries none. */
export const listItems = (block) => (Array.isArray(block?.items) ? block.items : []);

/** The plain text of a run list (link → "testo (URL)", cites optional). */
export function runsToPlain(runs, { cites = false } = {}) {
  let s = '';
  for (const r of asRuns(runs)) {
    switch (r.type) {
      case 'text': s += r.text; break;
      case 'br': s += '\n'; break;
      case 'cite': s = cites ? `${s}[${r.n}]` : dropCiteBlank(s); break;
      case 'strong':
      case 'em': s += runsToPlain(r.runs, { cites }); break;
      case 'link': {
        const label = runsToPlain(r.runs, { cites });
        s += !r.href || label === r.href ? (label || r.href) : `${label} (${r.href})`;
        break;
      }
      default: break;
    }
  }
  return s;
}

function listToPlain(block, opts) {
  return listItems(block)
    .map((it, i) => `${block.ordered ? `${i + 1}. ` : '• '}${runsToPlain(it, opts)}`)
    .join('\n');
}

export function slideTitle(block) {
  const label = i18n.t('outputs:pieces.slide', { n: block.number });
  const type = block.slideType ? i18n.t(`outputs:pieces.slideTypes.${block.slideType}`) : '';
  return type ? `${label} · ${type}` : label;
}

function slideToPlain(block, opts) {
  const lines = [slideTitle(block)];
  const headline = runsToPlain(block.headline, opts);
  if (headline) lines.push(headline);
  const body = blocksToPlain(block.body, opts);
  if (body) lines.push(body);
  if (block.visualCue) lines.push(`${i18n.t('outputs:pieces.visualCue')}: ${block.visualCue}`);
  return lines.join('\n');
}

/** Blocks → plain text; blocks separated by a blank line. */
export function blocksToPlain(blocks, opts = {}) {
  const out = [];
  for (const b of asBlocks(blocks)) {
    switch (b.type) {
      case 'heading':
      case 'paragraph': out.push(runsToPlain(b.runs, opts)); break;
      case 'list': out.push(listToPlain(b, opts)); break;
      case 'qa': {
        const q = runsToPlain(b.question, opts);
        const a = blocksToPlain(b.answer, opts);
        out.push([q, a].filter(Boolean).join('\n'));
        break;
      }
      case 'slide': out.push(slideToPlain(b, opts)); break;
      default: break;
    }
  }
  return out.filter((s) => s !== '').join('\n\n');
}

/**
 * A whole piece list as one plain-text document: the title on its own line,
 * then "=== Label ===" and the blocks of every piece ('source' pieces are
 * skipped — code does not belong in a .txt). Cites are kept when the list has
 * a 'sources' piece that explains them, dropped otherwise; pass `cites` to force.
 */
export function piecesToPlain(pieces, { title = '', cites } = {}) {
  const list = Array.isArray(pieces) ? pieces : [];
  const keepCites = typeof cites === 'boolean' ? cites : list.some((p) => p && p.id === 'sources');
  const parts = [];
  if (title) parts.push(String(title));
  for (const p of list) {
    if (!p || p.kind === 'source') continue;
    parts.push(`=== ${p.label} ===\n${blocksToPlain(p.blocks, { cites: keepCites })}`);
  }
  return parts.join('\n\n');
}
